import { FC, useEffect, useState } from "react";
import DashboardContentLayout from "../components/dashboard-content-layout";
import { useNavigate } from "react-router-dom";
import { AdRoutes } from "../../../constants/ad-routes";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Divider,
  InputAdornment,
  Step,
  StepButton,
  Stepper,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { generateRoute, makeSxStyles } from "ui";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import {
  ServiceTypeEnum,
  adminPostCustomerReqDtoSchema,
  adminPostLocationReqDtoSchema,
  useAdminLocationV2ControllerCreateLocationWithCustomer,
} from "service";
import {
  AdminPostCustomerReqDto,
  AdminPostLocationReqDto,
} from "service/api/estabrookSchemas";
import {
  WarningAmberOutlined,
  ArrowDropDown,
  ArrowDropUp,
} from "@mui/icons-material";
import { toFormikValidationSchema } from "zod-formik-adapter";
import useAddressValidation from "../../../hooks/use-address-validation";
import CrmDetailsFields, {
  CrmFieldValues,
} from "@/shared/components/crm/CrmDetailsFields";
import CustomerFinancesSection from "@/shared/components/crm/CustomerFinancesSection";
import { fieldLabelSx } from "@/shared/components/crm/form-typography";
import { useNumericFields } from "@/shared/hooks/useNumericFields";
import { numericFieldSx } from "@/shared/utils/numeric-input";
import { requiredNameValidations } from "@/shared/utils/crm-validation";
import HeaderItems, {
  HeaderItemsLeft,
  HeaderItemsRight,
} from "../../../shared/components/header-items/HeaderItems";
import Navbar from "../../../components/navbar/navbar";
import useUnsavedChangesBlocker from "../../../shared/hooks/useUnsavedChangesBlocker";
import { queryClient } from "../../../query-client";
import { createCustomerLocationsQueryKey } from "service/api/estabrookQueryKeys";

const steps = ["Personal information", "New Address"];

// Location + CRM fields (flat in the form). The customer + first location + CRM
// are created in ONE atomic call via the V2 create-with-customer endpoint, so
// there's no pre-created customer and no customerId on this form.
type AddressFormValues = Omit<AdminPostLocationReqDto, "customerId"> &
  CrmFieldValues;

const AddCustomerComboPage: FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { palette } = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [blockByUser, setBlockByUser] = useState(false);
  // CRM sections (Onsite Contact + Driveway Info) collapse behind Show More.
  const [showMore, setShowMore] = useState(false);
  const {
    getPlacePredictions,
    placePredictions,
    createAddressValidationSchema,
  } = useAddressValidation();

  // Address validation omits customerId — the V2 create-with-customer call
  // creates the customer, so there's no customerId to supply here.
  const customAddressValidationSchema = createAddressValidationSchema(
    adminPostLocationReqDtoSchema.omit({ customerId: true }),
  );

  const { mutate: createCustomerWithLocation, isLoading: isSubmitting } =
    useAdminLocationV2ControllerCreateLocationWithCustomer({
      onSuccess: (data) => {
        toast.success("Customer Created Successfully");
        customerForm.resetForm();
        addressForm.resetForm();
        setBlockByUser(false);
        const newCustomerId = data?.data?.customer?.id;
        // Invalidate the customer locations query so the new address appears in tabs
        if (newCustomerId) {
          queryClient.invalidateQueries(
            createCustomerLocationsQueryKey(newCustomerId),
          );
        }
        setTimeout(() => {
          navigate(
            generateRoute(AdRoutes.customerDetailAddress, {
              customerId: newCustomerId || "",
              addressId: data?.data?.id || "",
            }),
            { replace: true },
          );
        }, 10);
      },
    });

  const customerForm = useFormik<AdminPostCustomerReqDto>({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "+",
    },
    validationSchema: toFormikValidationSchema(
      adminPostCustomerReqDtoSchema.extend({ ...requiredNameValidations }),
    ),
    onSubmit: () => {
      // No API call here — the customer is created together with the first
      // address in a single V2 call on the next step. Just advance.
      setActiveStep(1);
      setBlockByUser(true);
    },
  });

  const addressForm = useFormik<AddressFormValues>({
    initialValues: {
      address: "",
      billingInformation: "",
      notes: "",
      serviceTime: 0,
      type: ServiceTypeEnum.annual,
      reportsEnabled: false,
      // CRM fields — sent inline on the V2 create-with-customer call.
      onsiteContactName: "",
      onsiteContactEmail: "",
      contractPrice: undefined,
      billingAddress: "",
      financialNotes: "",
      squareFootage: undefined,
      slope: undefined,
      condition: undefined,
      difficulty: undefined,
    },
    validationSchema: toFormikValidationSchema(customAddressValidationSchema),
    onSubmit: (values) =>
      createCustomerWithLocation({
        body: {
          // Customer (from step 1) + first location + CRM in one atomic call.
          email: customerForm.values.email.toLowerCase(),
          firstName: customerForm.values.firstName,
          lastName: customerForm.values.lastName,
          phone: customerForm.values.phone,
          address: values.address,
          serviceTime: values.serviceTime,
          type: values.type,
          reportsEnabled: values.reportsEnabled,
          notes: values.notes,
          onsiteContactName: values.onsiteContactName || undefined,
          onsiteContactEmail: values.onsiteContactEmail || undefined,
          contractPrice: values.contractPrice,
          billingAddress: values.billingAddress || undefined,
          financialNotes: values.financialNotes || undefined,
          squareFootage: values.squareFootage,
          slope: values.slope,
          condition: values.condition,
          difficulty: values.difficulty,
        },
      }),
  });

  useUnsavedChangesBlocker(
    customerForm.dirty || addressForm.dirty || blockByUser,
  );

  const numeric = useNumericFields({
    setFieldValue: addressForm.setFieldValue,
    setFieldTouched: addressForm.setFieldTouched,
    handleBlur: addressForm.handleBlur,
  });

  // A positive magnitude in minutes: no typed "-"/"e"/letters and no stepping,
  // with the schema's ">= 1" rule enforcing the sign. reportUntouched is off
  // because the form starts at 0, which fails that rule — the message waits for
  // the field to be used rather than greeting an untouched form.
  const serviceTime = numeric.field({
    name: "serviceTime",
    value: addressForm.values.serviceTime,
    error: addressForm.errors.serviceTime,
    touched: addressForm.touched.serviceTime,
    allowDecimal: true,
    reportUntouched: false,
  });

  // Square Footage and Difficulty sit behind "Show More", so collapsing the
  // section while one of them is invalid would gate Save with nothing on screen
  // explaining why. Depending on the flag rather than `errors` means this fires
  // on the transition only, leaving the section collapsible again afterwards.
  const hasHiddenNumericError = !!(
    addressForm.errors.squareFootage || addressForm.errors.difficulty
  );

  useEffect(() => {
    if (hasHiddenNumericError) setShowMore(true);
  }, [hasHiddenNumericError]);

  const handleStepClick = (index: number) => {
    if (index === activeStep) return;
    // Going back is always allowed; going forward validates the customer form
    // first (submitForm advances to step 1 on success).
    if (index < activeStep) {
      setActiveStep(index);
    } else {
      customerForm.submitForm();
    }
  };

  return (
    <DashboardContentLayout>
      <Navbar showAccountMenu>
        <HeaderItems>
          <HeaderItemsLeft
            breadcrumbs={[
              {
                label: "Customer Management",
                url: AdRoutes.customer,
              },
              {
                label: "Add New Customer",
              },
            ]}
          />
          <HeaderItemsRight>
            {activeStep === 1 && (
              <Button
                variant="outlined"
                onClick={() => setActiveStep(0)}
                disabled={isSubmitting}
                sx={{ mr: "12px" }}
                data-testid="back-to-customer-info"
              >
                Back
              </Button>
            )}
            <Button
              disabled={
                activeStep === 0
                  ? !customerForm.dirty || !customerForm.isValid
                  : isSubmitting || !addressForm.dirty || !addressForm.isValid
              }
              variant="contained"
              onClick={() =>
                activeStep === 0
                  ? customerForm.submitForm()
                  : addressForm.submitForm()
              }
              data-testid="new-customer-combo"
            >
              {activeStep === 0 ? "Next" : "Create Customer"}
            </Button>
          </HeaderItemsRight>
        </HeaderItems>
      </Navbar>
      <Box sx={{ maxWidth: "350px" }}>
        <Stepper
          nonLinear
          activeStep={activeStep}
          style={{ marginTop: "30px", marginBottom: "40px" }}
        >
          {steps.map((label, index) => (
            <Step key={label} completed={index < activeStep}>
              <StepButton
                color="inherit"
                onClick={() => handleStepClick(index)}
              >
                {label}
              </StepButton>
            </Step>
          ))}
        </Stepper>
      </Box>
      {activeStep === 0 && (
        <>
          <Box sx={styles.customerDetailContainer}>
            <Typography sx={styles.sectionTitle}>Customer Info</Typography>
            <Typography sx={fieldLabelSx}>First Name *</Typography>
            <TextField
              id="firstName"
              type="text"
              value={customerForm.values.firstName}
              onChange={customerForm.handleChange}
              onBlur={customerForm.handleBlur}
              helperText={
                customerForm.touched.firstName && customerForm.errors.firstName
              }
              error={
                customerForm.touched.firstName &&
                !!customerForm.errors.firstName
              }
              margin="dense"
              variant="outlined"
              fullWidth
            />
            <Typography sx={fieldLabelSx}>Last Name *</Typography>
            <TextField
              id="lastName"
              type="text"
              value={customerForm.values.lastName}
              onChange={customerForm.handleChange}
              onBlur={customerForm.handleBlur}
              helperText={
                customerForm.touched.lastName && customerForm.errors.lastName
              }
              error={
                customerForm.touched.lastName && !!customerForm.errors.lastName
              }
              margin="dense"
              variant="outlined"
              fullWidth
            />
            <Typography sx={fieldLabelSx}>Email Address *</Typography>
            <TextField
              id="email"
              type="email"
              value={customerForm.values.email}
              onChange={customerForm.handleChange}
              onBlur={customerForm.handleBlur}
              helperText={
                customerForm.touched.email && customerForm.errors.email
              }
              error={customerForm.touched.email && !!customerForm.errors.email}
              margin="dense"
              variant="outlined"
              fullWidth
            />
            <Typography sx={fieldLabelSx}>Phone number *</Typography>
            <TextField
              id="phone"
              type="tel"
              value={customerForm.values.phone}
              onChange={(e) => {
                const { value } = e.target;
                if (value.length === 0) {
                  customerForm.setFieldValue("phone", "+");
                } else {
                  customerForm.setFieldValue("phone", value);
                }
                customerForm.setFieldTouched("phone", true);
              }}
              onBlur={customerForm.handleBlur}
              helperText={
                customerForm.touched.phone && customerForm.errors.phone
              }
              error={customerForm.touched.phone && !!customerForm.errors.phone}
              margin="dense"
              variant="outlined"
              fullWidth
            />
          </Box>
          <Divider sx={{ mt: "40px", mb: "40px" }} />
          <Box display="flex">
            <WarningAmberOutlined
              sx={{ mr: "10px", color: palette.warning.main }}
            />
            <Typography fontWeight={600}>
              You will be able to add new addresses when new customer has been
              created.
            </Typography>
          </Box>
        </>
      )}
      {activeStep === 1 && (
        <Box sx={styles.customerDetailContainer}>
          <Typography sx={styles.sectionTitle}>Address Info</Typography>
          <Typography sx={fieldLabelSx}>Address *</Typography>
          <Autocomplete
            freeSolo
            filterOptions={(x) => x}
            options={placePredictions.map((prediction) => {
              return { label: prediction.description, value: prediction };
            })}
            value={addressForm.values.address}
            onChange={(_evt, value) => {
              if (value && typeof value === "object") {
                addressForm.setFieldValue("address", String(value.label));
                addressForm.setFieldTouched("address", true);
                // Immediately validate to clear any existing errors
                setTimeout(() => {
                  addressForm.validateField("address");
                }, 0);
              } else if (typeof value === "string") {
                addressForm.setFieldValue("address", value);
                addressForm.setFieldTouched("address", true);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                data-testid="address"
                name="address"
                error={
                  addressForm.touched.address && !!addressForm.errors.address
                }
                helperText={
                  addressForm.touched.address && addressForm.errors.address
                }
                onChange={(evt) => {
                  getPlacePredictions({ input: evt.target.value?.trim() });
                  addressForm.setFieldValue("address", evt.target.value);
                  addressForm.setFieldTouched("address", true);
                }}
                onBlur={() => {
                  addressForm.setFieldTouched("address", true);
                  addressForm.validateField("address");
                }}
              />
            )}
          />
          <Typography sx={fieldLabelSx}>Service Duration *</Typography>
          <TextField
            id="serviceTime"
            type="number"
            margin="dense"
            variant="outlined"
            value={serviceTime.value}
            onChange={serviceTime.onChange}
            onFocus={serviceTime.onFocus}
            onBlur={serviceTime.onBlur}
            error={serviceTime.showError}
            helperText={serviceTime.helperText}
            inputProps={serviceTime.inputProps}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">minutes</InputAdornment>
              ),
            }}
            sx={numericFieldSx}
          />
          <Typography sx={fieldLabelSx}>Snow Reporter</Typography>
          <Checkbox
            id="reportsEnabled"
            checked={addressForm.values.reportsEnabled}
            sx={{ justifySelf: "flex-start" }}
            onChange={addressForm.handleChange}
          />
          <Box sx={{ gridColumn: "1 / -1" }}>
            <CustomerFinancesSection
              values={addressForm.values}
              errors={addressForm.errors}
              touched={addressForm.touched}
              handleChange={addressForm.handleChange}
              handleBlur={addressForm.handleBlur}
              setFieldValue={addressForm.setFieldValue}
              setFieldTouched={addressForm.setFieldTouched}
            />
            {showMore && (
              <CrmDetailsFields
                values={addressForm.values}
                errors={addressForm.errors}
                touched={addressForm.touched}
                handleChange={addressForm.handleChange}
                handleBlur={addressForm.handleBlur}
                setFieldValue={addressForm.setFieldValue}
                setFieldTouched={addressForm.setFieldTouched}
                sectionOrder={["onsiteContact", "drivewayInfo"]}
              />
            )}
            <Button
              variant="text"
              onClick={() => setShowMore(!showMore)}
              endIcon={showMore ? <ArrowDropUp /> : <ArrowDropDown />}
              sx={{ mt: "8px" }}
              data-testid={showMore ? "show-less-button" : "show-more-button"}
            >
              {showMore ? "Show Less" : "Show More"}
            </Button>
          </Box>
        </Box>
      )}
    </DashboardContentLayout>
  );
};

const useStyles = makeSxStyles(() => ({
  sectionTitle: {
    gridColumn: "1 / -1",
    fontWeight: 400,
    fontSize: "22px",
    lineHeight: "28px",
    color: "#c5c6c9",
  },
  customerDetailContainer: {
    mt: "40px",
    display: "grid",
    // Match FieldRow (label 260 / gap 24 / input 360) so the fields align
    // with the CRM sections below.
    gridTemplateColumns: "260px 360px",
    columnGap: "24px",
    rowGap: "16px",
    alignItems: "center",
  },
}));

export default AddCustomerComboPage;
